import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SignpostIcon from '@mui/icons-material/Signpost';
import { Menu, Stack, Typography } from '@mui/material';
import { NodeVariants } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useTopNodeMenuStore from 'stores/top-node-menu-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  anchorEl: any | null;
  menuOpen: boolean;

  handleAddNode(nodeVariant: NodeVariants, nodeId: string, position: { x: number; y: number }, height: number, path: 'success' | 'failure'): void;

  handleCloseMenu(): void;
}

const FunctionItem: React.FC<{ icon: React.ReactNode; text: React.ReactNode; handleClick(): void }> = ({ icon, text, handleClick }) => {
  const { classes, cx } = useStyles();

  return (
    <Stack className={classes.functionItem} justifyContent={'center'} alignItems={'center'} onClick={handleClick} gap={1}>
      <Stack className={cx(classes.disabledAddIcon, 'function-icon-disabled')} justifyContent={'center'} alignItems={'center'}>
        <AddRoundedIcon />
      </Stack>
      {icon}
      <Typography className={cx(classes.functionText, 'function-text')}>{text}</Typography>
    </Stack>
  );
};

const TopNodeMenu: React.FC<Props> = ({ anchorEl, menuOpen, handleAddNode, handleCloseMenu }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { nodeId, position, height } = useTopNodeMenuStore((state) => ({
    nodeId: state.sourceNodeId,
    position: state.sourceNodePosition,
    height: state.sourceNodeHeight,
  }));

  return (
    <Menu
      classes={{ list: classes.menuList, paper: classes.menuPaper }}
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{ paper: { style: { width: 'auto' } } }}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      disableAutoFocusItem={true}
      disableRestoreFocus={true}
    >
      {!_.isNil(nodeId) && !_.isNil(position) && !_.isNil(height) ? (
        <Stack gap={2.5}>
          <Typography>{t('connectMessageTo')}</Typography>
          <Stack direction={'row'} gap={2}>
            <FunctionItem
              icon={<SignpostIcon className={cx('function-icon', 'success')} />}
              text={
                <Trans i18nKey={'positiveExit'}>
                  Condition
                  <span className={classes.successEdge}>positive</span> exit
                </Trans>
              }
              handleClick={() => {
                handleAddNode(NodeVariants.CONDITIONAL_NODE, nodeId, position, height, 'success');
                handleCloseMenu();
              }}
            />
            <FunctionItem
              icon={<SignpostIcon className={cx('function-icon', 'failure')} />}
              text={
                <Trans i18nKey={'negativeExit'}>
                  Condition
                  <span className={classes.failureEdge}>negative</span> exit
                </Trans>
              }
              handleClick={() => {
                handleAddNode(NodeVariants.CONDITIONAL_NODE, nodeId, position, height, 'failure');
                handleCloseMenu();
              }}
            />
          </Stack>
        </Stack>
      ) : (
        <Typography className={classes.infoText}>{t('noNodesMessage')}</Typography>
      )}
    </Menu>
  );
};

const useStyles = makeStyles()((theme) => ({
  menuPaper: {
    marginTop: '16px',
    borderRadius: '8px',
    boxShadow: Shadows.GREY_SHADOW_MAIN,
  },
  menuList: {
    padding: '16px',
  },
  infoText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  functionItem: {
    position: 'relative',
    cursor: 'pointer',
    width: '150px',
    borderRadius: '12px',
    border: `2px solid ${theme.palette.greys.lighter}`,
    padding: '24px 0 8px 0',
    '& .function-icon': {
      fontSize: theme.typography.pxToRem(40),
      color: theme.palette.greys.medium,
      '&.failure': {
        color: theme.palette.error.main,
      },
      '&.success': {
        color: theme.palette.success.main,
      },
    },
    '&:hover': {
      background: theme.palette.action.hover,
      border: `2px solid ${theme.palette.primary.main}`,
      '& .function-icon': {
        color: theme.palette.primary.main,
      },
      '& .function-icon-disabled': {
        background: theme.palette.primary.main,
      },
      '& .function-text': {
        color: theme.palette.text.primary,
      },
    },
  },
  failureEdge: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  successEdge: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  disabledAddIcon: {
    position: 'absolute',
    top: '-12px',
    padding: '2px',
    borderRadius: '50%',
    background: theme.palette.greys.lighter,
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      color: '#FFF',
    },
  },
  functionText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
  },
}));

export default TopNodeMenu;
